import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { AlertCircle, Key, Mail, Lock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { PoweredByCerebroBadge, PoweredByCerebroFooter } from '../components/PoweredByCerebro';
import axios from 'axios';
import { API_BASE } from '../config/api';

// Default Cerebro Logo - Teal/Gold Neural Brain Hexagon
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTokenLogin, setShowTokenLogin] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { setUser, login } = useAuth();
  const { tenant, isTenant, tenantSlug } = useTenant();
  const navigate = useNavigate();
  
  // Use tenant branding or defaults
  const logoUrl = tenant.branding?.logoUrl || DEFAULT_LOGO;
  const orgName = tenant.name || 'Cerebro Core';
  const primaryColor = tenant.branding?.primaryColor || '#D4AF37';

  // Keep the ?tenant= preview parameter across the redirect. Without it the
  // slug survives only in sessionStorage, so opening the dashboard in a new
  // tab silently falls back to the master platform's branding.
  const dashboardPath = new URLSearchParams(window.location.search).get('tenant')
    ? `/dashboard?tenant=${encodeURIComponent(tenantSlug)}`
    : '/dashboard';

  const handleTokenLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use relative URL so it works on any deployment slot
      const response = await axios.post(
        `${API_BASE}/api/admin-access`,
        { access_token: accessToken }
      );
      
      const { access_token: token, user } = response.data;
      
      // Store token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Update auth context
      if (setUser) {
        setUser(user);
      }
      
      toast.success('Admin access granted!');
      
      // Use window.location for hard redirect to ensure full page reload
      // This ensures AuthContext picks up the new token
      window.location.href = dashboardPath;
    } catch (err) {
      console.error('Token login error:', err);
      setError(err.response?.data?.detail || 'Invalid access token');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email.trim(), password);
      toast.success('Signed in successfully');
      navigate(dashboardPath);
    } catch (err) {
      console.error('Email login error:', err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    setError('');

    // SSO is per-tenant: the backend resolves the organisation's Entra
    // configuration from the slug in the URL. Without a tenant there is
    // nothing to sign in against, so point the user at their own link.
    if (!tenant?.slug) {
      setError(
        "Please use your organisation's Cerebro link to sign in with Microsoft. " +
        'Contact your administrator if you do not have it.'
      );
      return;
    }

    if (tenant.sso && tenant.sso.enabled === false) {
      setError('Microsoft sign-in is not enabled for your organisation. Contact your administrator.');
      return;
    }

    setLoading(true);

    // Full-page navigation rather than axios: this endpoint answers with a 302
    // to Microsoft, and XHR cannot follow a cross-origin redirect to a login page.
    window.location.href =
      `${API_BASE}/api/sso/login/${tenant.slug}?redirect_to=${encodeURIComponent('/dashboard')}`;
  };

  return (
    <div className="min-h-screen bg-navigate-dark flex flex-col" data-testid="login-page">
      <div className="flex flex-1">
        {/* Left Side - Form */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            {/* Logo */}
            <Link to="/" className="flex items-center justify-center gap-3 mb-8">
              <img src={logoUrl} alt={orgName} className="h-16 w-auto" />
              <span 
                className="font-heading text-2xl font-bold"
                style={{ color: primaryColor }}
              >
                {orgName === 'Cerebro' ? 'Cerebro Core' : orgName}
              </span>
            </Link>

            {/* Heading */}
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-bold text-white mb-2">
                Welcome to {orgName === 'Cerebro' ? 'Cerebro Core' : orgName}
              </h1>
              <p className="text-zinc-400">
                Sign in to continue your learning journey
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Admin Token Login Form */}
            {showTokenLogin ? (
              <form onSubmit={handleTokenLogin} className="space-y-6">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-amber-400">
                    <Key className="h-4 w-4 inline mr-2" />
                    Admin Access Mode
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="accessToken" className="text-zinc-300">Access Token</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                    <Input
                      id="accessToken"
                      type="password"
                      value={accessToken}
                      onChange={(e) => setAccessToken(e.target.value)}
                      placeholder="Enter your admin access token"
                      required
                      className="pl-10 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500 focus:border-navigate-gold focus:ring-navigate-gold"
                      data-testid="login-access-token"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ 
                    backgroundColor: primaryColor,
                    borderColor: primaryColor 
                  }}
                  data-testid="token-login-submit"
                >
                  {loading ? 'Signing in...' : 'Access Dashboard'}
                </button>
                
                <button
                  type="button"
                  onClick={() => { setShowTokenLogin(false); setError(''); }}
                  className="w-full text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  Back to login options
                </button>
              </form>
            ) : (
              /* Main Login Options */
              <div className="space-y-4">
                {/* Microsoft SSO Button - hidden for tenants with SSO switched off,
                    so the primary option on the page is one that can actually succeed.
                    Shown when sso is absent, since the master platform has no tenant record. */}
                {tenant.sso?.enabled !== false && (
                <button
                  onClick={handleMicrosoftLogin}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-[#2F2F2F] hover:bg-[#3F3F3F] text-white rounded-lg border border-zinc-700 transition-colors disabled:opacity-50"
                  data-testid="microsoft-login-btn"
                >
                  <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                  </svg>
                  <span className="font-medium">{loading ? 'Redirecting...' : 'Sign in with Microsoft'}</span>
                </button>
                )}

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-700"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-navigate-dark text-zinc-500">or</span>
                  </div>
                </div>

                {/* Email + Password - for accounts provisioned with a password */}
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-300">Email address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                        className="pl-10 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500 focus:border-navigate-gold focus:ring-navigate-gold"
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                      <Input
                        id="password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        className="pl-10 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500 focus:border-navigate-gold focus:ring-navigate-gold"
                        data-testid="login-password"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: primaryColor,
                      borderColor: primaryColor
                    }}
                    data-testid="email-login-submit"
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </button>
                </form>

                {/* Admin Access Token Button */}
                <button
                  onClick={() => { setShowTokenLogin(true); setError(''); }}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-transparent hover:bg-zinc-800/50 text-amber-400 rounded-lg border border-amber-400/30 transition-colors"
                  data-testid="token-login-link"
                >
                  <Key className="h-5 w-5" />
                  <span>Admin Access Token</span>
                </button>
              </div>
            )}

            {/* Help Text */}
            <p className="mt-8 text-center text-sm text-zinc-500">
              Contact your administrator for account access
            </p>
            
            {/* Powered by Cerebro Core Badge - For tenants */}
            {isTenant && (
              <div className="mt-6 flex justify-center">
                <PoweredByCerebroBadge />
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Image (hidden on mobile) */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-navigate-dark to-transparent z-10" />
          <img
            src={tenant.branding?.loginBackgroundUrl || "https://images.unsplash.com/photo-1772631653221-9c77e6f82c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9kZXJuJTIwY29ycG9yYXRlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc0NDQ4NjU5fDA&ixlib=rb-4.1.0&q=85"}
            alt="Corporate building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            <div className="text-center max-w-md px-8">
              <h2 className="font-heading text-3xl font-bold text-white mb-4">
                Build Capability, Not Just Knowledge
              </h2>
              <p className="text-zinc-300">
                Join thousands of professionals advancing their careers with 
                Cerebro Core — where learning meets intelligence.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <PoweredByCerebroFooter className="border-t border-zinc-800" />
    </div>
  );
}
